<template>
  <main
    class="flex flex-col min-h-[calc(100vh-200px)] items-center justify-center px-16 py-32"
    aria-labelledby="verify-email-heading"
  >
    <!-- Waiting UI keeps the tighter card chrome used by the apply/message modals. -->
    <div
      data-testid="verify-email-card"
      class="w-full max-w-[480px] mx-auto bg-foreground px-16 py-24 sm:rounded-xl sm:box-shadow-static-lg"
    >
      <VerifyEmailWaiting
        heading-id="verify-email-heading"
        :account-email="account?.email || ''"
        :show-pending-notices="true"
        @verified="onVerified"
      />
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted } from 'vue';
import { useStore } from 'vuex';
import VerifyEmailWaiting from '@/apps/verifyEmail/VerifyEmailWaiting.vue';
import { redirect } from '@/helpers/redirects';
import {
  resolvePostVerifyDestination,
  currentUserIsSitter,
  isSafeInternalPath,
  isVerifyEmailPath,
} from '@/apps/verifyEmail/helpers/paths';
import {
  clearEmailVerificationRequired,
  getPendingEmailVerificationReturnPath,
  isEmailVerificationPending,
  markEmailVerificationRequired,
} from '@/apps/verifyEmail/helpers/pendingEmailVerification';
import { getEmailVerificationPlacement, PLACEMENT_NO_VERIFICATION_REQUIRED } from '@/apps/verifyEmail/helpers/placementPolicy';
import type { Account } from '@/models/account';

defineOptions({ name: 'VerifyEmailPage' });

const store = useStore();
const instance = getCurrentInstance();

const account = computed((): Account | null => store.getters['account/getAccount'] || null);

onMounted(() => {
  armStickyTrapFromRoute();
});

function routeQuery(): Record<string, unknown> {
  const route = (instance?.proxy as { $route?: { query?: Record<string, unknown> } } | null)?.$route;
  return route?.query ?? {};
}

/**
 * Refresh an existing checkpoint's safe return path on mount.
 * A next query alone is not proof of a gate and must not trap Variant B.
 */
function armStickyTrapFromRoute(): void {
  if (!currentUserIsSitter()) {
    return;
  }

  if (!isEmailVerificationPending()) {
    return;
  }

  if (getEmailVerificationPlacement() === PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return;
  }

  const rawNext = routeQuery().next;
  const candidate = Array.isArray(rawNext) ? rawNext[0] : rawNext;
  const returnPath =
    typeof candidate === 'string' && isSafeInternalPath(candidate) && !isVerifyEmailPath(candidate)
      ? candidate
      : getPendingEmailVerificationReturnPath();

  markEmailVerificationRequired(returnPath);
}

function onVerified(): void {
  continueAfterVerification();
}

function continueAfterVerification(): void {
  const pendingReturnPath = getPendingEmailVerificationReturnPath();
  clearEmailVerificationRequired();
  redirect(resolvePostVerifyDestination(routeQuery(), currentUserIsSitter(), pendingReturnPath));
}
</script>
